import {
  drawingFileRel,
  drawingServerMessageSchema,
  elementIsNewer,
  type DrawingClientMessage,
  type DrawingElement,
  type DrawingFile,
  type DrawingPresence,
  type DrawingServerMessage,
} from '@hub/contracts'
import { buildWsRoomPath, getWsBaseUrl } from '@/lib/collab/ws-url'

/**
 * Client for a DrawingStore room (PLAN-080). Owns the socket, the local copy
 * of every element (newest version wins, same rule as the server), the
 * IndexedDB snapshot for instant reopen, image upload/download against the
 * workspace file routes, and presence. Framework-free; `DrawingBoard` binds
 * it to Excalidraw.
 */

export type DrawingStatus = 'connecting' | 'connected' | 'disconnected'

export interface DrawingClientEvents {
  /** Full scene (initial load, reconnect). `fromCache` = local copy, socket still syncing. */
  snapshot: (elements: DrawingElement[], fromCache: boolean) => void
  /** Elements changed by someone else, or the server's newer copy of ours. */
  remoteElements: (elements: DrawingElement[], actor: string) => void
  /** Image bytes arrived (fetched from R2) — hand them to Excalidraw. */
  files: (files: LoadedFile[]) => void
  presence: (presence: DrawingPresence[]) => void
  status: (status: DrawingStatus) => void
}

export interface LoadedFile {
  id: string
  mimeType: string
  created: number
  dataURL: string
}

const CACHE_DB = 'hub-drawings'
const CACHE_STORE = 'snapshots'
const RECONNECT_BASE_MS = 500
const RECONNECT_MAX_MS = 10_000
/** Elements per outgoing frame — keeps frames well under the 1 MiB limit. */
const SEND_BATCH = 200

type CachedScene = { rev: number; elements: DrawingElement[] }

function openCache(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(CACHE_DB, 1)
      req.onupgradeneeded = () => req.result.createObjectStore(CACHE_STORE)
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

async function cacheRead(key: string): Promise<CachedScene | null> {
  const db = await openCache()
  if (!db) return null
  return new Promise((resolve) => {
    try {
      const req = db.transaction(CACHE_STORE, 'readonly').objectStore(CACHE_STORE).get(key)
      req.onsuccess = () => resolve((req.result as CachedScene | undefined) ?? null)
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

async function cacheWrite(key: string, value: CachedScene): Promise<void> {
  const db = await openCache()
  if (!db) return
  try {
    db.transaction(CACHE_STORE, 'readwrite').objectStore(CACHE_STORE).put(value, key)
  } catch {
    // quota or private mode — the cache is a convenience
  }
}

export async function forgetDrawingCache(projectId: string, drawingId: string): Promise<void> {
  const db = await openCache()
  if (!db) return
  try {
    db.transaction(CACHE_STORE, 'readwrite')
      .objectStore(CACHE_STORE)
      .delete(`${projectId}:${drawingId}`)
  } catch {
    // ignore
  }
}

export class DrawingClient {
  readonly projectId: string
  readonly drawingId: string
  /** Every element we know of, tombstones included (Excalidraw needs them to reconcile). */
  elements = new Map<string, DrawingElement>()
  files = new Map<string, DrawingFile>()
  presence: DrawingPresence[] = []
  status: DrawingStatus = 'connecting'
  /** Our own connection id — excluded from the collaborator list. */
  self: string | null = null
  synced = false

  #ws: WebSocket | null = null
  #closed = false
  #reconnectAttempt = 0
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null
  #snapshotBuffer: DrawingElement[] = []
  #loadedFiles = new Set<string>()
  #uploading = new Set<string>()
  #cacheTimer: ReturnType<typeof setTimeout> | null = null
  #listeners: { [K in keyof DrawingClientEvents]: Set<DrawingClientEvents[K]> } = {
    snapshot: new Set(),
    remoteElements: new Set(),
    files: new Set(),
    presence: new Set(),
    status: new Set(),
  }

  constructor(projectId: string, drawingId: string) {
    this.projectId = projectId
    this.drawingId = drawingId
  }

  get cacheKey() {
    return `${this.projectId}:${this.drawingId}`
  }

  on<K extends keyof DrawingClientEvents>(event: K, fn: DrawingClientEvents[K]): () => void {
    this.#listeners[event].add(fn)
    return () => this.#listeners[event].delete(fn)
  }

  #emit<K extends keyof DrawingClientEvents>(
    event: K,
    ...args: Parameters<DrawingClientEvents[K]>
  ) {
    for (const fn of this.#listeners[event]) (fn as (...a: unknown[]) => void)(...args)
  }

  async connect(): Promise<void> {
    this.#closed = false
    const cached = await cacheRead(this.cacheKey)
    if (cached && !this.synced && this.elements.size === 0) {
      for (const el of cached.elements) this.elements.set(el.id, { ...el })
      this.#emit('snapshot', cached.elements, true)
    }
    this.#open()
  }

  #open() {
    if (this.#closed) return
    this.#setStatus('connecting')
    const url = `${getWsBaseUrl()}/${buildWsRoomPath(this.projectId, 'drawing', this.drawingId)}`
    const ws = new WebSocket(url)
    this.#ws = ws
    ws.onopen = () => {
      this.#reconnectAttempt = 0
      this.#setStatus('connected')
    }
    ws.onmessage = (ev) => {
      let msg: DrawingServerMessage
      try {
        msg = drawingServerMessageSchema.parse(JSON.parse(String(ev.data)))
      } catch (err) {
        console.error('[drawing-client] bad server message', err)
        return
      }
      this.#handle(msg)
    }
    ws.onclose = (ev) => {
      if (this.#ws === ws) this.#ws = null
      this.#setStatus('disconnected')
      // 1001 "… deleted" is the store telling us it is gone — do not resurrect it.
      if (ev.code === 1001 && /deleted/.test(ev.reason)) {
        this.#closed = true
        return
      }
      this.#scheduleReconnect()
    }
    ws.onerror = () => {
      // onclose follows
    }
  }

  #scheduleReconnect() {
    if (this.#closed || this.#reconnectTimer) return
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.#reconnectAttempt++)
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null
      this.#open()
    }, delay)
  }

  close() {
    this.#closed = true
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer)
    this.#reconnectTimer = null
    if (this.#cacheTimer) clearTimeout(this.#cacheTimer)
    this.#cacheTimer = null
    this.#ws?.close(1000, 'closed')
    this.#ws = null
  }

  #setStatus(status: DrawingStatus) {
    if (this.status === status) return
    this.status = status
    this.#emit('status', status)
  }

  #sendRaw(msg: DrawingClientMessage) {
    if (this.#ws?.readyState === WebSocket.OPEN) this.#ws.send(JSON.stringify(msg))
  }

  #handle(msg: DrawingServerMessage) {
    switch (msg.t) {
      case 'snapshot': {
        if (msg.chunk.i === 0) {
          this.#snapshotBuffer = []
          if (msg.self) this.self = msg.self
          if (msg.presence) this.#setPresence(msg.presence)
          if (msg.files) this.#setFiles(msg.files)
        }
        this.#snapshotBuffer.push(...msg.elements)
        if (msg.chunk.i < msg.chunk.n - 1) return
        // Reconnect: anything we changed while offline that is newer than
        // the server's copy stays and is re-sent.
        const merged = new Map<string, DrawingElement>()
        for (const el of this.#snapshotBuffer) merged.set(el.id, { ...el })
        const pendingLocal: DrawingElement[] = []
        for (const el of this.elements.values()) {
          if (!elementIsNewer(el, merged.get(el.id))) continue
          merged.set(el.id, el)
          if (this.synced) pendingLocal.push(el)
        }
        this.elements = merged
        this.#snapshotBuffer = []
        this.synced = true
        this.#scheduleCacheWrite(msg.rev)
        this.#emit(
          'snapshot',
          [...merged.values()].map((el) => ({ ...el })),
          false
        )
        for (let i = 0; i < pendingLocal.length; i += SEND_BATCH) {
          this.#sendRaw({ t: 'elements', elements: pendingLocal.slice(i, i + SEND_BATCH) })
        }
        break
      }
      case 'elements': {
        const applied: DrawingElement[] = []
        for (const el of msg.elements) {
          if (!elementIsNewer(el, this.elements.get(el.id))) continue
          // Excalidraw mutates scene elements in place — keep our own copy so
          // later version comparisons are against what the server has.
          this.elements.set(el.id, { ...el })
          applied.push(el)
        }
        if (applied.length > 0) {
          this.#scheduleCacheWrite(msg.rev)
          this.#emit('remoteElements', applied, msg.actor)
        }
        break
      }
      case 'files':
        this.#setFiles(msg.files)
        break
      case 'presence':
        this.#setPresence(msg.presence)
        break
      case 'error':
        console.error('[drawing-client]', msg.message)
        break
    }
  }

  #setPresence(presence: DrawingPresence[]) {
    this.presence = presence.filter((p) => p.connId !== this.self)
    this.#emit('presence', this.presence)
  }

  #setFiles(files: DrawingFile[]) {
    for (const f of files) this.files.set(f.id, f)
    void this.#fetchMissingFiles(files)
  }

  #scheduleCacheWrite(rev: number) {
    if (this.#cacheTimer) return
    this.#cacheTimer = setTimeout(() => {
      this.#cacheTimer = null
      void cacheWrite(this.cacheKey, { rev, elements: [...this.elements.values()] })
    }, 1_000)
  }

  /** Elements we changed locally (already in the scene). Newest version wins. */
  sendElements(elements: DrawingElement[]): void {
    const changed: DrawingElement[] = []
    for (const el of elements) {
      if (!elementIsNewer(el, this.elements.get(el.id))) continue
      const copy = { ...el }
      this.elements.set(el.id, copy)
      changed.push(copy)
    }
    if (changed.length === 0) return
    for (let i = 0; i < changed.length; i += SEND_BATCH) {
      this.#sendRaw({ t: 'elements', elements: changed.slice(i, i + SEND_BATCH) })
    }
    this.#scheduleCacheWrite(-1)
  }

  sendPresence(patch: Omit<Extract<DrawingClientMessage, { t: 'presence' }>, 't'>): void {
    this.#sendRaw({ t: 'presence', ...patch })
  }

  // ── files ─────────────────────────────────────────────────────────────

  #fileUrl(fileId: string): string {
    return `/workspaces/${this.projectId}/drawings/${drawingFileRel(this.drawingId, fileId)}`
  }

  /** Upload image bytes Excalidraw just added (id = content hash → idempotent). */
  async uploadFile(file: { id: string; mimeType: string; dataURL: string; created: number }) {
    if (this.files.has(file.id) || this.#uploading.has(file.id)) return
    this.#uploading.add(file.id)
    this.#loadedFiles.add(file.id) // it is already in the scene
    try {
      const blob = await (await fetch(file.dataURL)).blob()
      const res = await fetch(this.#fileUrl(file.id), {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': file.mimeType, 'x-hub-file-created': String(file.created) },
        body: blob,
      })
      if (!res.ok) throw new Error(`upload failed: ${res.status}`)
      this.files.set(file.id, {
        id: file.id,
        mimeType: file.mimeType,
        created: file.created,
        size: blob.size,
      })
    } catch (err) {
      console.error('[drawing-client] image upload failed', err)
    } finally {
      this.#uploading.delete(file.id)
    }
  }

  async #fetchMissingFiles(files: DrawingFile[]) {
    const missing = files.filter((f) => !this.#loadedFiles.has(f.id))
    if (missing.length === 0) return
    for (const f of missing) this.#loadedFiles.add(f.id)
    const loaded = await Promise.all(
      missing.map(async (f): Promise<LoadedFile | null> => {
        try {
          const res = await fetch(this.#fileUrl(f.id), { credentials: 'include' })
          if (!res.ok) throw new Error(String(res.status))
          const blob = await res.blob()
          const dataURL = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(String(reader.result))
            reader.onerror = () => reject(reader.error)
            reader.readAsDataURL(blob)
          })
          return { id: f.id, mimeType: f.mimeType, created: f.created, dataURL }
        } catch (err) {
          console.error('[drawing-client] image fetch failed', f.id, err)
          this.#loadedFiles.delete(f.id)
          return null
        }
      })
    )
    const ok = loaded.filter((f): f is LoadedFile => f !== null)
    if (ok.length > 0) this.#emit('files', ok)
  }
}
